import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { BaseComponent } from "src/ancestors/base/base.component";
import { ApeAdminImpersonateTemplateComponent } from "../../../templates/admin-impersonate-template/admin-impersonate-template.component";
import { ImpersonifiableUserSummary } from "alloy-performance-engine-cl";
import { Router } from "@angular/router";
import { ImpersonationService } from "../../../services/impersonation.service";
import { ImpersonateApiService } from "../../../services/api/impersonate-api.service";
import { ApplicationError } from "sharedclasses";
import { UrlCollection } from "../../../constants/url-collection";

/**
 * Componente admin impersonate - gestisce l'impersonazione degli utenti
 */
@Component({
  selector: "app-admin-impersonate",
  standalone: true,
  imports: [CommonModule, ApeAdminImpersonateTemplateComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./admin-impersonate.component.html"
})
export class AdminImpersonateComponent extends BaseComponent implements OnInit, OnDestroy {
  /**
   * Chiavi per salvare stato in sessione
   */
  static readonly SESSION_KEYS = {
    SEARCH_VALUE: "adminImpersonate_searchValue",
    PAGE: "adminImpersonate_page",
    PAGE_SIZE: "adminImpersonate_pageSize"
  } as const;

  /**
   * Flag per indicare se mostrare il skeleton loader
   */
  showSkeletonLoader = false;

  /**
   * Lista degli utenti recuperati dal backend
   */
  users: ImpersonifiableUserSummary[] = [];

  /**
   * Valore della ricerca corrente
   */
  searchValue = "";

  /**
   * Stato di loading per i dati
   */
  loading = false;

  /**
   * Pagina corrente per la paginazione
   */
  page = 1;

  /**
   * Dimensione pagina per la paginazione
   */
  pageSize = 50;

  /**
   * Numero totale di elementi
   */
  totalItems = 0;

  constructor(
    private impersonationService: ImpersonationService,
    private impersonateApiService: ImpersonateApiService,
    override router: Router
  ) {
    super();
  }

  /**
   * Inizializza il componente e carica gli utenti
   */
  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // Controlla se ci sono dati di sessione da ripristinare
    const savedSearchValue = sessionStorage.getItem(AdminImpersonateComponent.SESSION_KEYS.SEARCH_VALUE);
    const savedPage = sessionStorage.getItem(AdminImpersonateComponent.SESSION_KEYS.PAGE);
    const savedPageSize = sessionStorage.getItem(AdminImpersonateComponent.SESSION_KEYS.PAGE_SIZE);

    let stateRestored = false;

    if (savedSearchValue !== null || savedPage !== null || savedPageSize !== null) {
      // Ripristina lo stato salvato
      if (savedSearchValue !== null) {
        this.searchValue = savedSearchValue;
      }
      if (savedPage !== null) {
        this.page = parseInt(savedPage, 10);
      }
      if (savedPageSize !== null) {
        this.pageSize = parseInt(savedPageSize, 10);
      }

      stateRestored = true;

      // Pulisce la sessione dopo 3 secondi
      setTimeout(() => {
        sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.SEARCH_VALUE);
        sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.PAGE);
        sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.PAGE_SIZE);
      }, 3000);
    }

    if (stateRestored) {
      // Carica con i parametri ripristinati
      await this.onSearch({
        page: this.page,
        pageSize: this.pageSize,
        searchText: this.searchValue,
        isSearch: true
      });
    } else {
      // Altrimenti carica normalmente
      void this.loadUsers();
    }
  }

  /**
   * Pulizia quando il componente viene distrutto
   */
  override async ngOnDestroy(): Promise<void> {
    await super.ngOnDestroy();
  }

  /**
   * Carica la lista degli utenti impersonabili
   */
  private async loadUsers(): Promise<void> {
    this.showSkeletonLoader = true;
    this.loading = true;

    try {
      // Carica count e lista per il caricamento iniziale
      this.totalItems = await this.impersonateApiService.getImpersonifiableUsersCount();
      if (this.totalItems > 0) {
        const fromRecord = (this.page - 1) * this.pageSize;
        this.users = await this.impersonateApiService.getImpersonifiableUsersList(undefined, fromRecord, this.pageSize);
      } else {
        this.users = [];
      }
    } catch (error: unknown) {
      if (error instanceof ApplicationError) {
        this.logger.error("Error loading users for impersonate", error.message);
      } else {
        this.logger.error("Unexpected error loading users for impersonate", String(error));
      }
      this.users = [];
    } finally {
      this.showSkeletonLoader = false;
      this.loading = false;
    }
  }

  /**
   * Gestisce il cambio della ricerca
   */
  onSearchChange(value: string): void {
    this.searchValue = value;
  }

  /**
   * Gestisce l'esecuzione della ricerca
   */
  async onSearch(params: {
    page: number;
    pageSize: number;
    searchText?: string;
    isSearch?: boolean;
  }): Promise<void> {
    this.page = params.page;
    this.pageSize = params.pageSize;
    this.searchValue = params.searchText || "";
    this.loading = true;
    this.showSkeletonLoader = true;

    try {
      // Chiama il count solo se è una ricerca (non paginazione)
      if (params.isSearch) {
        this.totalItems = await this.impersonateApiService.getImpersonifiableUsersCount(this.searchValue || undefined);
      }

      if (this.totalItems > 0) {
        // Carica la lista solo se ci sono risultati
        const fromRecord = (this.page - 1) * this.pageSize;
        this.users = await this.impersonateApiService.getImpersonifiableUsersList(this.searchValue || undefined, fromRecord, this.pageSize);
      } else {
        this.users = [];
      }
    } catch (error: unknown) {
      this.logger.error("Error during search", error instanceof Error
        ? error.message
        : String(error));
      this.users = [];
    } finally {
      this.loading = false;
      this.showSkeletonLoader = false;
    }
  }

  /**
   * Gestisce l'impersonazione del manager
   */
  async onImpersonateManager(user: ImpersonifiableUserSummary): Promise<void> {
    if (!user.impersonableManagerId) {
      this.logger.error(`Manager ID not available for user ${user.userId}`);
      return;
    }

    this.logger.info(`Impersonating manager of user: ${user.forename} ${user.surname}`);

    // Salva lo stato corrente prima dell'impersonazione
    this.saveCurrentState();

    await this.impersonationService.handleImpersonation(user, user.impersonableManagerId, { path: UrlCollection.adminImpersonate });
  }

  /**
   * Gestisce l'impersonazione dell'employee
   */
  async onImpersonateEmployee(user: ImpersonifiableUserSummary): Promise<void> {
    this.logger.info(`Impersonating employee: ${user.forename} ${user.surname}`);

    // Salva lo stato corrente prima dell'impersonazione
    this.saveCurrentState();

    await this.impersonationService.handleImpersonation(user, user.userId, { path: UrlCollection.adminImpersonate });
  }

  /**
   * Salva lo stato corrente nella sessione
   */
  private saveCurrentState(): void {
    sessionStorage.setItem(AdminImpersonateComponent.SESSION_KEYS.SEARCH_VALUE, this.searchValue);
    sessionStorage.setItem(AdminImpersonateComponent.SESSION_KEYS.PAGE, this.page.toString());
    sessionStorage.setItem(AdminImpersonateComponent.SESSION_KEYS.PAGE_SIZE, this.pageSize.toString());
  }
}
